import { collection, getDocs, getDoc, doc, setDoc, deleteDoc, query, where } from 'firebase/firestore';
import * as firestorePaths from './firestorePaths';
import { normalizeEmailToDocId } from './emailNormalization';
import { getArtifactProjectId } from '../firebaseInit';
import { getEffectiveAgentLimit, SUBSCRIPTION_STATUSES } from './usageTracking';

// Helper function to find which team a user belongs to
// Prefer caller-provided teamId (from claims). Avoid listing teams for non-admins.
export const findUserTeam = async (db, appId, userId, preferredTeamId = null) => {
  try {
    if (preferredTeamId) {
      const teamUserRef = doc(db, firestorePaths.getUsersPath(appId, preferredTeamId), userId);
      const teamUserDoc = await getDoc(teamUserRef);
      if (teamUserDoc.exists()) return preferredTeamId;
      return null;
    }
    // As a last resort, attempt listing teams (may be denied by rules)
    const teamsRef = collection(db, firestorePaths.getTeamsRoot(appId));
    const teamsSnapshot = await getDocs(teamsRef);
    for (const teamDoc of teamsSnapshot.docs) {
      try {
        const teamUserRef = doc(db, firestorePaths.getUsersPath(appId, teamDoc.id), userId);
        const teamUserDoc = await getDoc(teamUserRef);
        if (teamUserDoc.exists()) return teamDoc.id;
      } catch (error) {
        // Skip teams we cannot read
        continue;
      }
    }
    return null;
  } catch (error) {
    console.warn(`Error finding team for user ${userId}:`, error);
    return null;
  }
};

// Shared Firebase operations
export const loadTeamsAndUsers = async (db, firebaseConfig, userRole, userEmail, userTeamFromClaims = null) => {
  if (!db) return { teams: [], users: [] };
    
  try {
    const appId = getArtifactProjectId();
        
    // Load teams: admins can list all; team leads/users use their own team from claims
    const teamsData = [];
    if (userRole === 'admin') {
      const teamsRef = collection(db, firestorePaths.getTeamsRoot(appId));
      const teamsSnapshot = await getDocs(teamsRef);
      teamsSnapshot.forEach(doc => {
        teamsData.push({ id: doc.id, ...doc.data() });
      });
    } else if (userTeamFromClaims) {
      const teamRef = doc(db, firestorePaths.getTeamPath(appId, userTeamFromClaims));
      const teamSnap = await getDoc(teamRef);
      if (teamSnap.exists()) {
        teamsData.push({ id: userTeamFromClaims, ...teamSnap.data() });
      } else {
        // If we cannot read the team doc, still return the id to allow user listings
        teamsData.push({ id: userTeamFromClaims, name: userTeamFromClaims });
      }
    }
        
    // Load users from team-containerized structure
    let usersData = [];
    let teamsToQuery = [];
        
    if (userRole === 'team_lead') {
      teamsToQuery = teamsData.map(team => team.id);
    } else if (userRole === 'admin') {
      // Admins see users from all teams
      teamsToQuery = teamsData.map(team => team.id);
    }
        
    // For each team, load users from the team's user collection
    for (const teamId of teamsToQuery) {
      try {
        const teamUsersRef = collection(db, firestorePaths.getUsersPath(appId, teamId));
        const teamUsersSnapshot = await getDocs(teamUsersRef);
                
        teamUsersSnapshot.forEach(doc => {
          const userData = doc.data();
          // Fallback: If email is missing from data, try to reconstruct from document ID
          // Document IDs are normalized emails (e.g., joe_example_com -> joe@example.com)
          let email = userData.email;
          if (!email && doc.id) {
            // Try to denormalize: replace last underscore before domain with @, and other underscores with .
            // This is a best-effort fallback and may not always be accurate
            const parts = doc.id.split('_');
            if (parts.length >= 3) {
              const domain = parts.slice(-2).join('.');
              const localPart = parts.slice(0, -2).join('.');
              email = `${localPart}@${domain}`;
            } else {
              email = doc.id; // Just use the ID as-is if we can't parse it
            }
            console.warn(`⚠️ User ${doc.id} missing email field, reconstructed as: ${email}`);
          }
          usersData.push({
            id: doc.id,
            teamId: teamId, // Ensure teamId is set from the path
            email: email, // Use reconstructed email if original was missing
            ...userData
          });
        });
      } catch (error) {
        console.warn(`Error loading users for team ${teamId}:`, error);
        // Continue with other teams even if one fails
      }
    }
        
    console.log('📊 Loaded teams and users:', { teams: teamsData.length, users: usersData.length });
        
    return { teams: teamsData, users: usersData };
  } catch (error) {
    console.error('Error loading teams and users:', error);
    return { teams: [], users: [] };
  }
};

export const createTeam = async (db, firebaseConfig, teamName, leadEmail, options = {}) => {
  if (!teamName.trim()) throw new Error('Team name is required');
    
  const { industryId, industryDescription, firstName, lastName } = options;
  const appId = getArtifactProjectId();
  const teamId = `team_${Date.now()}`;
  const teamRef = doc(db, `artifacts/${appId}/teams`, teamId);
    
  const teamData = {
    name: teamName,
    leadEmail: leadEmail,
    createdAt: new Date(),
  };

  if (industryId) teamData.industryId = industryId;
  if (industryDescription) teamData.industryDescription = industryDescription;
  if (firstName) teamData.leadFirstName = firstName;
  if (lastName) teamData.leadLastName = lastName;
    
  await setDoc(teamRef, teamData);
    
  return teamId;
};

/**
 * Get the current count of agents (basic role users) in a team
 */
export const getTeamAgentCount = async (db, teamId) => {
  if (!teamId) return 0;
  
  const appId = getArtifactProjectId();
  const teamUsersRef = collection(db, firestorePaths.getUsersPath(appId, teamId));
  const usersSnapshot = await getDocs(teamUsersRef);
  
  let agentCount = 0;
  usersSnapshot.forEach(doc => {
    const userData = doc.data();
    // Count only basic role users (agents)
    if (userData.role === 'basic') {
      agentCount++;
    }
  });
  
  return agentCount;
};

/**
 * Check if team can add more agents based on subscription
 * Returns { canAdd: boolean, message: string, currentCount: number, limit: number|null }
 */
export const checkAgentLimit = async (db, teamId) => {
  if (!teamId) {
    return { canAdd: false, message: 'Team ID is required', currentCount: 0, limit: 0 };
  }
  
  const appId = getArtifactProjectId();
  
  // Get team subscription
  const teamRef = doc(db, firestorePaths.getTeamPath(appId, teamId));
  const teamDoc = await getDoc(teamRef);
  
  if (!teamDoc.exists()) {
    return { canAdd: false, message: 'Team not found', currentCount: 0, limit: 0 };
  }
  
  const teamData = teamDoc.data();
  const subscription = teamData.subscription || {};
  
  // Lifetime users have unlimited agents
  if (subscription.status === SUBSCRIPTION_STATUSES.LIFETIME) {
    const currentCount = await getTeamAgentCount(db, teamId);
    return { 
      canAdd: true, 
      message: 'Unlimited agents (Lifetime license)', 
      currentCount, 
      limit: null,
      isUnlimited: true
    };
  }
  
  // Trial users cannot add agents
  if (subscription.status === SUBSCRIPTION_STATUSES.TRIALING) {
    return { 
      canAdd: false, 
      message: 'Upgrade to a paid plan to add team members', 
      currentCount: 0, 
      limit: 0,
      isTrialing: true
    };
  }
  
  // Get effective agent limit (includes any purchased extra agents)
  const limit = getEffectiveAgentLimit(subscription);
  
  // Null limit means unlimited
  if (limit === null) {
    const currentCount = await getTeamAgentCount(db, teamId);
    return { 
      canAdd: true, 
      message: 'Unlimited agents', 
      currentCount, 
      limit: null,
      isUnlimited: true
    };
  }
  
  const currentCount = await getTeamAgentCount(db, teamId);
  const canAdd = currentCount < limit;
  
  return {
    canAdd,
    currentCount,
    limit,
    remaining: Math.max(0, limit - currentCount),
    message: canAdd 
      ? `${limit - currentCount} agent slot${limit - currentCount !== 1 ? 's' : ''} available`
      : `Agent limit reached (${currentCount}/${limit}). Purchase additional agents or upgrade your plan.`,
    isUnlimited: false
  };
};

/**
 * Invites a user to a team. The Firestore writes complete immediately so the
 * caller can update its UI right away. The signup-URL generation (Cloud Function
 * call) runs asynchronously and delivers the URL via the optional `onSignupUrl`
 * callback when it resolves — which can take several minutes on a cold start.
 *
 * @param {function} [onSignupUrl] - Called with the signupUrl once the CF responds.
 */
export const inviteUser = async (db, firebaseConfig, userData, getEmailDocumentId, skipLimitCheck = false, onSignupUrl = null) => {
  const { email, role, teamId, template, invitedBy } = userData;
  if (!email.trim()) throw new Error('User email is required');
    
  // CRITICAL: Normalize email to lowercase to prevent case sensitivity bugs
  const normalizedEmail = email.toLowerCase().trim();
    
  const appId = getArtifactProjectId();
  const emailDocId = getEmailDocumentId(normalizedEmail);
  
  // Write to team-containerized location (authoritative source)
  if (!teamId || !teamId.trim()) {
    throw new Error('Team ID is required - all users must be assigned to a team');
  }
  
  // Check agent limit for basic role (unless explicitly skipped, e.g., for admins)
  if (role === 'basic' && !skipLimitCheck) {
    const limitCheck = await checkAgentLimit(db, teamId);
    
    if (!limitCheck.canAdd) {
      const error = new Error(limitCheck.message);
      error.code = 'AGENT_LIMIT_REACHED';
      error.limitInfo = limitCheck;
      throw error;
    }
  }
  
  // Get team name for email
  let teamName = teamId;
  try {
    const teamRef = doc(db, firestorePaths.getTeamPath(appId, teamId));
    const teamDoc = await getDoc(teamRef);
    if (teamDoc.exists()) {
      teamName = teamDoc.data().name || teamId;
    }
  } catch (error) {
    console.warn('Could not fetch team name for invitation:', error);
  }
    
  const userDataToSave = {
    email: normalizedEmail,
    role: role || 'basic',
    invitedBy,
    invitedAt: new Date(),
    defaultTemplate: template || 'example-script',
    status: 'invited'
  };
    
  if (teamId && teamId.trim()) {
    userDataToSave.teamId = teamId;
    userDataToSave.teamName = teamName;
  }
    
  const teamUserRef = doc(db, firestorePaths.getUsersPath(appId, teamId), emailDocId);
  await setDoc(teamUserRef, userDataToSave, { merge: true });
    
  // Also write to global pending invitations index for easier validation during signup
  const globalInvitationRef = doc(db, `artifacts/${appId}/pendingInvitations`, emailDocId);
  await setDoc(globalInvitationRef, {
    email: normalizedEmail,
    teamId,
    invitedBy,
    invitedAt: new Date(),
    status: 'invited'
  }, { merge: true });
  
  // Fire-and-forget: generate the signup URL via Cloud Function.
  // The CF can take several minutes on cold start — running it asynchronously
  // means callers get immediate feedback (UI update) without blocking on the CF.
  // The signupUrl is delivered to the caller via the onSignupUrl callback.
  (async () => {
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      // 10-minute timeout — the CF can cold-start slowly on the test environment.
      const sendInvitation = httpsCallable(functions, 'sendAgentInvitation', { timeout: 600000 });
      
      const result = await sendInvitation({
        email: normalizedEmail,
        teamId,
        teamName
      });
      
      console.log('✅ Invitation token created for', emailDocId);
      const signupUrl = result.data?.signupUrl || null;
      if (signupUrl && onSignupUrl) {
        onSignupUrl(signupUrl);
      }
    } catch (inviteError) {
      console.warn('⚠️ Could not create invitation token:', inviteError.message);
    }
  })();
    
  return { emailDocId };
};

export const updateUserTemplate = async (db, firebaseConfig, userId, newTemplate, preferredTeamId = null) => {
  const appId = getArtifactProjectId();
    
  // Update team-containerized location (authoritative source)
  const teamId = await findUserTeam(db, appId, userId, preferredTeamId);
  if (!teamId) {
    throw new Error('User not found in any team - cannot update template');
  }
    
  const teamUserRef = doc(db, firestorePaths.getUsersPath(appId, teamId), userId);
  // SIMPLIFIED: Everyone uses defaultTemplate now
  await setDoc(teamUserRef, {
    defaultTemplate: newTemplate,
    updatedAt: new Date()
  }, { merge: true });
};

export const bulkAssignTemplate = async (db, firebaseConfig, userIds, template, userIdToTeamId = {}) => {
  const appId = getArtifactProjectId();
  if (!Array.isArray(userIds) || userIds.length === 0) return { updated: 0, failed: 0 };
    
  const updateOne = async (userId) => {
    try {
      const teamId = userIdToTeamId[userId] || await findUserTeam(db, appId, userId);
      if (!teamId) {
        return { userId, ok: false, reason: 'team-not-found' };
      }
      const teamUserRef = doc(db, firestorePaths.getUsersPath(appId, teamId), userId);
      // SIMPLIFIED: Everyone uses defaultTemplate now
      await setDoc(teamUserRef, { 
        defaultTemplate: template,
        updatedAt: new Date()
      }, { merge: true });
      return { userId, ok: true };
    } catch (error) {
      console.warn(`bulkAssignTemplate: failed for ${userId}`, error);
      return { userId, ok: false, reason: 'write-failed' };
    }
  };
    
  const results = await Promise.all(userIds.map(updateOne));
  const updated = results.filter(r => r.ok).length;
  const failed = results.length - updated;
  if (failed > 0) {
    console.warn(`bulkAssignTemplate: ${failed}/${results.length} updates failed`);
  }
  return { updated, failed };
};

export const updateUserRole = async (db, firebaseConfig, userId, newRole) => {
  const appId = getArtifactProjectId();
    
  // Update team-containerized location (authoritative source)
  const teamId = await findUserTeam(db, appId, userId);
  if (!teamId) {
    throw new Error('User not found in any team - cannot update role');
  }
    
  const teamUserRef = doc(db, firestorePaths.getUsersPath(appId, teamId), userId);
  await setDoc(teamUserRef, {
    role: newRole
  }, { merge: true });
};

export const removeUserFromTeam = async (db, firebaseConfig, userId, preferredTeamId = null) => {
  const appId = getArtifactProjectId();
    
  // Find user in team containers and remove them.
  // Pass preferredTeamId so Team Leads don't need list-all-teams permission.
  const teamId = await findUserTeam(db, appId, userId, preferredTeamId);
  if (!teamId) {
    throw new Error('User not found in any team');
  }
    
  // Get user data before deletion to clean up invitation and revoke access
  const teamUserRef = doc(db, firestorePaths.getUsersPath(appId, teamId), userId);
  const userDoc = await getDoc(teamUserRef);
  const userData = userDoc.exists() ? userDoc.data() : null;
  const userEmail = userData?.email;
    
  // Delete user from team
  await deleteDoc(teamUserRef);
    
  // Clean up pending invitation if it exists, allowing re-invitation
  if (userEmail) {
    const emailDocId = normalizeEmailToDocId(userEmail);
    const pendingInvitationRef = doc(db, `artifacts/${appId}/pendingInvitations`, emailDocId);
    try {
      await deleteDoc(pendingInvitationRef);
      console.log(`✅ Cleaned up pending invitation for ${emailDocId}`);
    } catch (error) {
      // Invitation may not exist, that's fine
      console.log(`ℹ️ No pending invitation found for ${emailDocId}`);
    }
        
    // CRITICAL: Revoke user's Firebase Auth claims and force sign-out
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const revokeUserAccess = httpsCallable(functions, 'revokeUserAccess');
            
      console.log(`🔐 Revoking Firebase Auth access for ${userEmail}...`);
      const result = await revokeUserAccess({ userEmail });
            
      if (result.data.success) {
        console.log(`✅ Firebase Auth access revoked for ${userEmail}`);
        console.log('🔒 User will be signed out and cannot log back in');
      }
    } catch (revokeError) {
      // Log but don't fail - the user is already removed from Firestore
      console.error('⚠️ Failed to revoke Firebase Auth access:', revokeError);
      console.log('ℹ️ User data removed, but they may still be able to log in briefly');
    }
  }
    
  console.log(`✅ Removed user ${userId} from team ${teamId} and revoked all access`);
  console.log('ℹ️ User can be re-invited to any team');
};

export const activateUser = async (db, firebaseConfig, userId) => {
  // This function is deprecated - user activation now handled via Custom Claims
  console.log('⚠️ activateUser is deprecated - use Custom Claims management');
  throw new Error('Function deprecated - user activation handled via Custom Claims');
};

export const deleteTeam = async (db, firebaseConfig, teamId, users) => {
  try {
    const appId = getArtifactProjectId();
    const teamRef = doc(db, `artifacts/${appId}/teams/${teamId}`);
        
    // Remove all users from this team first
    const usersToUpdate = users.filter(user => user.teamId === teamId);
        
    if (usersToUpdate.length > 0) {
      // Legacy user cleanup is no longer needed - team deletion handled via team containers
      console.log(`ℹ️ Skipping legacy user cleanup for ${usersToUpdate.length} users (now handled via Custom Claims)`);
    } else {
      console.log(`ℹ️ No users found in team ${teamId}`);
    }
        
    await deleteDoc(teamRef);
        
    console.log(`✅ Team ${teamId} deleted successfully`);
  } catch (error) {
    console.error('❌ Error deleting team:', error);
    throw error; // Re-throw to let the calling function handle it
  }
};

// Utility functions
export const getRoleDisplayName = (role) => {
  switch (role) {
    case 'admin': return 'Super Admin';
    case 'team_lead': return 'Team Lead';
    case 'basic': return 'Agent';
    default: return role;
  }
};

// Enhanced status logic that considers whether user has actually signed up
export const getActualUserStatus = (user) => {
  // If user is explicitly marked as inactive, respect that status
  if (user.status === 'inactive') {
    return 'inactive';
  }
    
  // Only consider user active if they have the explicit signup completion flag
  if (user.signupCompleted === true) {
    return 'active';
  }
    
  // For backward compatibility, check if they have clear authentication evidence
  // But be more strict about what constitutes "completed signup"
  if (user.createdAt && user.lastAuthenticated) {
    // Only if they have BOTH timestamps, consider them active
    return 'active';
  }
    
  // Don't trust the status field alone - it might be manually set
  // Only trust it if it's explicitly 'active' AND they have auth evidence
  if (user.status === 'active' && (user.createdAt || user.lastAuthenticated)) {
    return 'active';
  }
    
  // Default to invited if no clear, reliable signup completion indicators
  return 'invited';
};

// Check if a user has actually completed signup by looking at their Firestore auth data
export const checkUserSignupStatus = async (db, firebaseConfig, userEmail) => {
  // This function is deprecated - signup status now determined via Custom Claims
  console.log('⚠️ checkUserSignupStatus is deprecated - use Custom Claims');
  return true; // Assume completed for backwards compatibility
};

// Legacy function - no longer used with Custom Claims
// User status is now managed via Firebase Auth Custom Claims
export const updateUserOnAuthentication = async (db, firebaseConfig, userEmail) => {
  console.log('⚠️ updateUserOnAuthentication called but disabled - using Custom Claims for user management');
  // This function is kept for backward compatibility but does nothing
  // All user role and status management is now handled via Firebase Auth Custom Claims
};

// Check and update existing users who might have incomplete status information
export const checkAndUpdateExistingUsers = async (db, firebaseConfig) => {
  // This function is deprecated - user status now managed via Custom Claims
  console.log('⚠️ checkAndUpdateExistingUsers is deprecated - use Custom Claims management');
  return 0; // Return 0 updated users for backwards compatibility
};

// Helper function to get email document ID (if not already imported)
const getEmailDocumentId = (email) => {
  if (!email) {
    console.error('❌ getEmailDocumentId called with undefined/null email');
    return null;
  }
  if (typeof email !== 'string') {
    console.error('❌ getEmailDocumentId called with non-string email:', email);
    return null;
  }
    
  console.log('🔍 getEmailDocumentId processing email:', email ? '[REDACTED]' : 'null');
    
  // Use centralized normalization function
  const processedEmail = normalizeEmailToDocId(email);
    
  console.log('🔍 getEmailDocumentId result:', processedEmail);
    
  return processedEmail;
};

export const getStatusDisplayName = (status) => {
  switch (status) {
    case 'active': return 'Active';
    case 'invited': return 'Pending Sign Up';
    case 'inactive': return 'Inactive';
    default: return status;
  }
};

export const getStatusBadge = (status) => {
  const baseClasses = 'px-2 py-1 rounded-full text-xs font-medium';
  switch (status) {
    case 'active': return `${baseClasses} bg-green-100 text-green-800`;
    case 'invited': return `${baseClasses} bg-yellow-100 text-yellow-800`;
    case 'inactive': return `${baseClasses} bg-gray-100 text-gray-800`;
    default: return `${baseClasses} bg-gray-100 text-gray-800`;
  }
};

export const getRoleBadge = (role) => {
  const baseClasses = 'px-2 py-1 rounded-full text-xs font-medium';
  switch (role) {
    case 'admin': return `${baseClasses} bg-red-100 text-red-800`;
    case 'team_lead': return `${baseClasses} bg-blue-100 text-blue-800`;
    case 'basic': return `${baseClasses} bg-gray-100 text-gray-800`;
    default: return `${baseClasses} bg-gray-100 text-gray-800`;
  }
};

export const getTeamUsers = (users, teamId) => {
  return users.filter(user => user.teamId === teamId && user.role === 'basic');
};
